import React, { useState, useRef, useLayoutEffect, useEffect, useMemo, useCallback } from 'react';
import 'tippy.js/dist/tippy.css';
import * as d3 from 'd3';
import KPIChart from './KPIChart';
import KPITooltip from './KPITooltip';
import { formatValue, getContrastingTextColor, sanitizeTooltipContent, formatDate } from './formatUtils';
import './KPIComponent.css';
import './PaginationDots.css';

const KPIComponent = React.memo(({ 
  data, 
  setAnimationDuration, 
  animationDuration, 
  onBoxSelect,
  containerWidth,
  selectedPillarId,
  pillarColors = {}
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [boxesPerView, setBoxesPerView] = useState(1);
  const [boxWidth, setBoxWidth] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [hoveredData, setHoveredData] = useState({});
  const [isSwiping, setIsSwiping] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const selectedKeyRef = useRef(null);

  const getColor = useCallback((pillar, framework, pillarId) => {
    // Check for custom pillar color first
    if (pillarColors[pillarId]) {
      // Return a light background version of the pillar color
      try {
        const hexColor = pillarColors[pillarId];
        // Convert hex to RGB
        const r = parseInt(hexColor.substring(1,3), 16);
        const g = parseInt(hexColor.substring(3,5), 16);
        const b = parseInt(hexColor.substring(5,7), 16);
        // Return with low opacity for background
        return `rgba(${r}, ${g}, ${b}, 0.1)`;
      } catch (error) {
        console.error('Error processing custom pillar color:', error);
      }
    }
    
    // Check if framework exists and is earthquake (case insensitive)
    if (framework && framework.toLowerCase() === 'earthquake') {
      try {
        return `rgba(0, 0, 100, 0.02)`;
      } catch (error) {
        console.error('Error processing color:', error);
        return 'transparent';
      }
    }
    return 'transparent';
  }, [pillarColors]);

  const getTextColor = useCallback(() => {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary').trim();
  }, []);

  useLayoutEffect(() => {
    const calculateBoxes = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth - 2;
        const minBoxWidth = 267;
        const newBoxesPerView = Math.floor(containerWidth / minBoxWidth) || 1;
        const newBoxWidth = containerWidth / newBoxesPerView;
        setBoxesPerView(newBoxesPerView);
        setBoxWidth(newBoxWidth);
      }
    };
    calculateBoxes();
    window.addEventListener('resize', calculateBoxes);
    return () => window.removeEventListener('resize', calculateBoxes);
  }, [containerWidth, data]);

  useEffect(() => {
    // KPI component state update
    
    // Debug: Simplified data structure check (verbose logs removed)
    
    // Skip if no data
    if (data.length === 0) return;
    
    // Skip processing if resetting is in progress (animation duration is 0)
    if (animationDuration === 0) return;
    
    // Track what's happening for debugging
    let newIndex = null;
    
    // Priority 1: If a specific pillar id is requested, find and select it
    if (selectedPillarId) {
      // Try multiple search strategies to find the pillar
      // NOTE: kpiData items use 'pillarId' property, not 'pillar_id'
      const matchIndexById = data.findIndex(item => item.id === selectedPillarId);
      const matchIndexByPillarId = data.findIndex(item => item.pillarId === selectedPillarId);
      const matchIndexByIdString = data.findIndex(item => String(item.id) === String(selectedPillarId));
      const matchIndexByPillarIdString = data.findIndex(item => String(item.pillarId) === String(selectedPillarId));
      
      // Use the best match found
      let matchIndex = -1;
      if (matchIndexById >= 0) {
        matchIndex = matchIndexById;
      } else if (matchIndexByPillarId >= 0) {
        matchIndex = matchIndexByPillarId;
      } else if (matchIndexByIdString >= 0) {
        matchIndex = matchIndexByIdString;
      } else if (matchIndexByPillarIdString >= 0) {
        matchIndex = matchIndexByPillarIdString;
      }
      
      if (matchIndex >= 0) {
        newIndex = matchIndex;
      }
    }
    
    // Priority 2: If no selection exists yet (first load) or the data has changed (view change),
    // select the first item by default
    if (newIndex === null && (selectedIndex === null || selectedPillarId === '')) {
      newIndex = 0;
      // Defaulting to first item
    }
    
    // Only update state if we're making a change
    if (newIndex !== null && newIndex !== selectedIndex) {
      setSelectedIndex(newIndex);
      
      // Store the key for future reference
      if (data[newIndex] && data[newIndex].key) {
        selectedKeyRef.current = data[newIndex].key;
      }
    }
  }, [data, selectedPillarId, selectedIndex, animationDuration, data.length]);

  // Handle view resets and animations separately from selection logic
  useEffect(() => {
    // Reset view position and animation settings when data changes (new view loads)
    setCurrentIndex(0);
    setAnimationDuration(0);
    
    // Reset hover states
    setHoveredData({});
    setShowTooltip(false);
    
    // Re-enable animations after reset
    const timer = setTimeout(() => {
      setAnimationDuration(750);
      setShowTooltip(true);
    }, 10);

    return () => clearTimeout(timer);
  }, [data]); // Depend on data itself to catch all changes, not just length

  // Ensure the selected item is visible by scrolling to it if needed
  // Only auto-scroll when selectedIndex changes, not when currentIndex changes
  useEffect(() => {
    if (selectedIndex !== null && boxesPerView > 0) {
      // Only scroll to show selected item if it was changed by pillar selection
      // NOTE: Use pillarId to match selectedPillarId, not id
      if (selectedPillarId && (selectedPillarId === data[selectedIndex]?.pillarId || selectedPillarId === data[selectedIndex]?.id)) {
        const pageIndex = Math.floor(selectedIndex / boxesPerView);
        const currentPageIndex = Math.floor(currentIndex / boxesPerView);
        if (pageIndex !== currentPageIndex) {
          // Auto-scrolling to show selected item
          setCurrentIndex(pageIndex * boxesPerView);
        }
      }
    }
  }, [selectedIndex, boxesPerView, selectedPillarId, data]); // Removed currentIndex from dependencies

  const getCurrentPage = (index, itemsPerView, totalItems) => {
    const maxIndex = Math.max(0, totalItems - itemsPerView);
    let page;
    if (index >= maxIndex) {
      page = Math.ceil(totalItems / itemsPerView) - 1;
    } else {
      page = Math.floor(index / itemsPerView);
    }
    return page;
  };

  const handleNext = () => {
    setAnimationDuration(750);
    setShowTooltip(false);
    const totalItems = data.length;
    const maxIndex = Math.max(0, totalItems - boxesPerView);
    const nextIndex = Math.min(currentIndex + boxesPerView, maxIndex);
    setCurrentIndex(nextIndex);
  };

  const handlePrev = () => {
    setShowTooltip(false);
    const prevIndex = Math.max(currentIndex - boxesPerView, 0);
    setCurrentIndex(prevIndex);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
    setIsSwiping(true);
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    handleSwipeGesture();
    setIsSwiping(false);
  };

  const handleSwipeGesture = () => {
    const swipeThreshold = 50;
    if (touchStartX.current - touchEndX.current > swipeThreshold) {
      handleNext();
    }
    if (touchEndX.current - touchStartX.current > swipeThreshold) {
      handlePrev();
    }
  };

  const handleBoxClick = (index) => {
    if (onBoxSelect && data[index]) {
      // Set the selected index locally
      setSelectedIndex(index);
      
      // Store the key of the selected item
      const item = data[index];
      if (item && item.key) {
        selectedKeyRef.current = item.key;
      } else {
        selectedKeyRef.current = null; // Clear the key if not available
      }
      
      // Always call onBoxSelect to notify parent with the FULL item
      onBoxSelect(item);
    }
  };

  // Memoize the carousel container style
  const carouselContainerStyle = useMemo(() => ({
    width: containerWidth || '100%',
    overflow: 'hidden',
    position: 'relative',
    height: 173
  }), [containerWidth]);

  // Memoize the content style
  const contentStyle = useMemo(() => ({
    width: `${boxWidth * data.length}px`,
    transform: `translateX(-${currentIndex * boxWidth}px)`,
    transition: `transform ${animationDuration}ms ease-in-out`,
  }), [boxWidth, data.length, currentIndex, animationDuration]);

  // Update pagination dots click handler
  const handlePaginationClick = (pageIndex) => {
    setAnimationDuration(750);
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / boxesPerView);
    
    if (pageIndex >= totalPages) {
      return;
    }
    
    let newIndex;
    if (pageIndex === totalPages - 1) {
      newIndex = Math.max(0, totalItems - boxesPerView);
    } else {
      newIndex = pageIndex * boxesPerView;
    }
    
    setCurrentIndex(newIndex);
  };

  return (
    <div style={carouselContainerStyle}>
    <div className="kpi-container" ref={containerRef}>
      {currentIndex > 0 && (
        <button className="kpi-arrow left" onClick={handlePrev}>&#8249;</button>
      )}
      {currentIndex < Math.max(0, data.length - boxesPerView) && (
        <button className="kpi-arrow right" onClick={handleNext}>&#8250;</button>
      )}

      <div
        className="kpi-wrapper"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="kpi-content"
          style={contentStyle}
        >
          {/* KPI Items */}
          {data.map((item, index) => (
            <div
              className={`kpi-item ${selectedIndex === index ? 'selected' : ''}`}
              key={item.id || item.pillarId || `${item.pillar}_${index}`}
              onClick={() => handleBoxClick(index)}
              style={{ 
                backgroundColor: getColor(item.pillar, item.framework, item.pillarId || item.id), 
                width: `${boxWidth}px`, 
                color: getTextColor()
              }}
            >
              <div className="kpi-body kpiZoom">
                <div className="kpi-header">
                  <h3 className="kpi-title" style={{ color: getTextColor() }}>
                    {item.pillar_name || item.pillar}
                  </h3>
                  {item.value !== 0 && item.value !== null && item.value !== undefined && (
                    <KPITooltip 
                      index={index} 
                      title={item.indicator}
                      content={sanitizeTooltipContent(item.description || item.context || 'No description available')} 
                      disabled={isSwiping} 
                    />
                  )}
                </div>
                {item.indicator && (
                  <div 
                    className="kpi-subtitle" 
                    style={{ 
                      color: getTextColor(),
                      opacity: 0.7 
                    }}
                  >
                    {item.indicator}
                  </div>
                )}
                <p onClick={() => handleBoxClick(index)}>
                  {hoveredData[index]?.value !== undefined
                    ? formatValue(hoveredData[index]?.value, item.valueType)
                    : formatValue(item.value, item.valueType)}
                  {item.valueType !== 'percentage' && item.valueType !== 'number' && item.valueType !== 'N/A' && (
                    <span className="value-type">{item.valueType}</span>
                  )}
                </p>
              </div>
              {item.trend && (
                <KPIChart
                  data={item.trend}
                  colorBg={getColor(item.pillar, item.framework, item.pillarId || item.id)}
                  color={getTextColor()}
                  index={index}
                  setHoveredData={setHoveredData}
                />
              )}
              <div className="kpi-footer">
                <div className="date-source kpiZoom">
                  <span className="date">
                    {(hoveredData[index]?.date || item.date) && formatDate(hoveredData[index]?.date || item.date)}
                  </span>
                  {item.value !== 0 && item.value !== null && item.value !== undefined && (
                    <span className="source">
                      {hoveredData[index]?.source || item.source || 'No source available'}
                    </span>
                  )}
                </div>
              </div>
              <div className="reportName">
                {hoveredData[index]?.reportName || item.sourceDetails?.reportName || ''}
              </div>
              {selectedIndex === index && <div className="underline" />}
            </div>
          ))}
        </div>
      </div>
    </div>
    
    <div className="pagination-dots-bg kpiZoom">
     {Math.ceil(data.length / boxesPerView) > 1 && (
       <div className="pagination-dots">
       {Array.from({ length: Math.ceil(data.length / boxesPerView) }, (_, pageIndex) => {
         const currentPage = getCurrentPage(currentIndex, boxesPerView, data.length);
         const isActive = currentPage === pageIndex;
         
         return (
           <span
             key={pageIndex}
             className={`pagination-dot ${isActive ? 'active' : ''}`}
             onClick={() => handlePaginationClick(pageIndex)}
           />
         );
       })}
     </div>
     )}
   </div>
   </div>
  );
});

KPIComponent.displayName = 'KPIComponent';

export default KPIComponent;